"use client"

import { useState, useEffect } from "react"
import { usePathname } from "next/navigation"
import Button from "@mui/material/Button"
import type { components } from "umbraco"
import { getMediaUrl, resolveLinkHref } from "umbraco"
import { Link, getNonUmbracoUrl } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import Container from "../../Container"
import BellIcon from "../../icons/BellIcon"
import CloseIcon from "../../icons/CloseIcon"
import { useHeroStripSlot } from "./Stack"
import {
  Root,
  Inner,
  TitleRow,
  TitleGroup,
  TitleText,
  CloseButton,
  IconWrapper,
  IconImage,
  ContentRow,
  BodyText,
} from "./styles"

type HeroStripData =
  components["schemas"]["HeroStripBlockPropertiesModel"]

interface Props {
  data: HeroStripData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  /** EGDE-596: this strip's own block id. A page may hold more than one strip,
   *  each with its own close button, so dismissal is scoped per strip — one
   *  shared key meant closing either bar suppressed both on the next load. */
  blockId?: string
  /** EGDE-596: this strip's position in CMS order, used by the stack to decide
   *  which of several eligible strips takes the one visible slot. */
  stripOrder?: number
  [key: string]: unknown
}

const STORAGE_KEY = "heroStrip_dismissed"

export default function HeroStrip({ data, blockId, stripOrder = 0 }: Props) {
  const pathname = usePathname()
  const isHomepage = pathname === "/" || /^\/[a-z]{2}\/?$/.test(pathname)
  const [isEligible, setIsEligible] = useState(false)
  const storageKey = blockId ? `${STORAGE_KEY}:${blockId}` : STORAGE_KEY

  useEffect(() => {
    if (data.isActive === false) return

    if (data.expiryDate) {
      const expiry = new Date(data.expiryDate)
      const now = new Date()
      // Compare date-only: banner stays visible the entire expiry day,
      // hidden starting the next day
      const expiryDay = new Date(
        expiry.getFullYear(),
        expiry.getMonth(),
        expiry.getDate(),
      )
      const today = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
      )
      if (expiryDay < today) return
    }

    try {
      if (sessionStorage.getItem(storageKey) === "true") return
    } catch {
      // sessionStorage unavailable
    }

    // EGDE-318: while the hero brand splash is playing (homepage first load)
    // this fixed banner shows through the splash's transparent aleph window —
    // hold it back until the intro reports it is done. The blocks hydrate
    // from separate lazy chunks, so this banner often mounts BEFORE the hero:
    // the splash's data-hero-intro marker may not exist yet. Watch for it
    // appearing (MutationObserver) instead of only checking once; a fallback
    // timer covers pages where no splash ever declares itself.
    if (!isHomepage) {
      setIsEligible(true)
      return
    }

    const html = document.documentElement
    let shown = false
    const cleanups: Array<() => void> = []
    const show = () => {
      if (shown) return
      shown = true
      cleanups.forEach((fn) => fn())
      setIsEligible(true)
    }

    const onIntroFinished = () => show()
    window.addEventListener("hero-intro-finished", onIntroFinished)
    cleanups.push(() =>
      window.removeEventListener("hero-intro-finished", onIntroFinished),
    )

    if (!html.hasAttribute("data-hero-intro")) {
      // The splash hasn't mounted (yet). If it declares itself, keep
      // waiting for its finish event; if it never does, show after a beat.
      const fallback = setTimeout(show, 1500)
      cleanups.push(() => clearTimeout(fallback))
      const observer = new MutationObserver(() => {
        if (html.hasAttribute("data-hero-intro")) {
          clearTimeout(fallback)
          observer.disconnect()
        }
      })
      observer.observe(html, {
        attributes: true,
        attributeFilter: ["data-hero-intro"],
      })
      cleanups.push(() => observer.disconnect())
    }

    return () => cleanups.forEach((fn) => fn())
  }, [data.isActive, data.expiryDate, isHomepage, storageKey])

  // EGDE-596: QA's ruling is that two strips never share the screen — "1st one
  // appear, when closing it the 2nd one appears". Reporting eligibility to the
  // stack lets it hand its single slot to the earliest eligible strip; releasing
  // the slot on dismissal is what promotes the next one.
  const hasSlot = useHeroStripSlot(storageKey, stripOrder, isEligible)

  const handleDismiss = () => {
    setIsEligible(false)
    try {
      sessionStorage.setItem(storageKey, "true")
    } catch {
      // sessionStorage unavailable
    }
  }

  if (!hasSlot) return null

  const ctaResolved = resolveLinkHref(data.ctaUrl, {
    getNonUmbracoUrl,
    getUmbracoMediaUrl,
  })
  const href = ctaResolved?.href ?? null
  const ctaLinkType = ctaResolved?.isInternal ? "Content" : "External"
  const ctaTarget = ctaResolved?.target

  const iconRel = getMediaUrl(data.icon)
  const iconUrl = iconRel ? getUmbracoMediaUrl(iconRel) : null

  return (
    <Root role="banner" data-block="hero-strip" $clearHeader={isHomepage}>
      <Container paddingMobile>
        <Inner>
          {/* RTL row: source [TitleGroup, CloseButton] renders visually as [CloseButton LEFT, TitleGroup RIGHT] */}
          <TitleRow>
            <TitleGroup>
              {/* EGDE-491: bell sits at the inline-start edge — visually to the
                  RIGHT of the title in RTL — so the icon comes first in source */}
              <IconWrapper>
                {iconUrl ? (
                  <IconImage src={iconUrl} alt="" />
                ) : (
                  <BellIcon />
                )}
              </IconWrapper>
              <TitleText variant="body1">{data.title}</TitleText>
            </TitleGroup>
            <CloseButton
              onClick={handleDismiss}
              aria-label="Dismiss banner"
              type="button"
            >
              <CloseIcon />
            </CloseButton>
          </TitleRow>

          {/* RTL row: source [BodyText, CTA] renders visually as [CTA LEFT, BodyText RIGHT] */}
          <ContentRow>
            {data.bodyText && (
              <BodyText variant="body1">{data.bodyText}</BodyText>
            )}
            {data.ctaLabel && href && (
              <Button
                variant="primary"
                size="large"
                {...(ctaLinkType === "Content"
                  ? { LinkComponent: Link }
                  : {})}
                href={href}
                target={ctaTarget}
                rel={ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
              >
                {data.ctaLabel}
              </Button>
            )}
          </ContentRow>
        </Inner>
      </Container>
    </Root>
  )
}
